import React, { ChangeEvent, FormEvent, Fragment, useEffect, useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from 'react-router-dom';

import Loader from "../../components/Loader";
import LocaleSelect from "../../components/Locale/LocaleSelect";
import { AppDispatch, AppState } from "../../store/configureStore";
import { loginUser, loginWithProvider, setAuthProvider } from "../../store/users/auth";
import { MsalAuthProvider } from "store/users/authProviders/MsalAuthProvider";
import { KeycloakAuthProvider } from "store/users/authProviders/KeycloakAuthProvider";
import { clearAuthError } from "store/users/auth";

import faviconOixio from "../../css/img/favicon.png";

import "./LoginPage.css";

const LoginPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    
    const { t } = useTranslation(["pages", "common"]);

    const { loading, error } = useSelector((state: AppState) => state.users.auth);
    const [searchParams, setSearchParams] = useSearchParams();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [email, setEmail] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [errorMessage, setErrorMessage] = useState<string>("");

    const externalError = error?.message;

    useEffect(() => {
        dispatch(clearAuthError());
    }, [dispatch]);

    useEffect(() => {
        const urlError = searchParams.get('error');
        if (urlError) {
            setErrorMessage(urlError);

            // Optional: Remove error from URL after reading
            searchParams.delete('error');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const onChangeEmail = (event: ChangeEvent<HTMLInputElement>) => {
        setEmail(event.target.value);
    };

    const onChangePassword = (event: ChangeEvent<HTMLInputElement>) => {
        setPassword(event.target.value);
    };

    const onSubmit = async (event: FormEvent) => {
        event.preventDefault();

        setSubmitting(true);
        setErrorMessage("");

        if (!email) {
            setSubmitting(false);
            setErrorMessage(t("login.errorEmail"));
            return;
        }

        if (!password) {
            setSubmitting(false);
            setErrorMessage(t("login.errorPassword"));
            return;
        }

        dispatch(loginUser(email, password));
        setSubmitting(false);
    };

    const onADLogin = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        const hadFailedAttempt = !!errorMessage || !!externalError;
        setErrorMessage("");

        const provider = new KeycloakAuthProvider();
        setAuthProvider(provider);
        dispatch(loginWithProvider(hadFailedAttempt));
    };

    const onEntraLogin = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        setErrorMessage("");
        setAuthProvider(new MsalAuthProvider());
        dispatch(loginWithProvider());
    };

    const IsDevelopment = process.env.NODE_ENV === "development";

    const displayedError = errorMessage || externalError;

    return (
        <Fragment>
            {loading && <Loader />}
            <div className="container-fluid">
                <Card className="login-clean no-border mt-5">
                    <Card.Body>
                        <Row>
                            <form className="login-clean-form" onSubmit={onSubmit} noValidate>
                                <Row>
                                    <Col className="mb-2 ms-auto">
                                        <LocaleSelect />
                                    </Col>
                                </Row>
                                <Row className="justify-content-center">
                                    <img
                                        src={faviconOixio}
                                        alt="oixio favicon"
                                        className="favicon_login"
                                    />
                                    <span className="logo-title">
                                        {" "}
                                        {t("common:document.title")}
                                    </span>
                                </Row>
                                <>
                                    <div className="input-error-login">{displayedError}</div>
                                    <button
                                        type="button"
                                        className="btn btn-login shadow-sm mt-3 mb-4 visual-selection-aid"
                                        onClick={onADLogin}
                                    >
                                        {t("login.submit")}
                                    </button>
                                </>
                                <div className="divider-login"></div>
                                <>
                                    {IsDevelopment && <>
                                        <div>
                                            <div className="form-group">
                                                <input
                                                    className="form-control login-email-input visual-selection-aid"
                                                    type="email"
                                                    name="email"
                                                    autoComplete="off"
                                                    placeholder={t("login.email")}
                                                    value={email}
                                                    onChange={onChangeEmail}
                                                />
                                            </div>
                                            <div className="form-group">
                                                <input
                                                    className="form-control login-password-input mt-2 visual-selection-aid"
                                                    type="password"
                                                    name="password"
                                                    autoComplete="off"
                                                    placeholder={t("login.password")}
                                                    value={password}
                                                    onChange={onChangePassword}
                                                />
                                            </div>
                                            <div className="input-error-login">{errorMessage}</div>
                                        </div>
                                        <Row className="mt-2">
                                            <a
                                                href="/ForgotPassword"
                                                className="btn-forgotPassword visual-selection-aid"
                                                role="button"
                                            >
                                                {t("login.forgotPassword")}
                                            </a>
                                        </Row>
                                        <Row>
                                            <button
                                                type="submit"
                                                className="btn btn-login shadow-sm mt-3 mb-3 visual-selection-aid"
                                                disabled={submitting}
                                            >
                                                {t("login.submit")}
                                            </button>
                                            <br />
                                        </Row>
                                        <div className="divider-login"></div>
                                    </>}
                                    <button
                                        type="button"
                                        className="btn-microsoftAD visual-selection-aid"
                                        onClick={onEntraLogin}
                                    >
                                        <img
                                            className="logo-MicrosoftAD"
                                            src={process.env.PUBLIC_URL + "/microsoft-logo.svg"}
                                            alt=""
                                        />
                                        <span className="text-login-Microsoft">
                                            {t("login.microsoft")}
                                        </span>
                                    </button>
                                </>
                            </form>
                        </Row>
                    </Card.Body>
                </Card>
            </div>
        </Fragment>
    );
};

export default LoginPage;
